/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx, css } from '@emotion/react'
import { capitalize } from 'lodash'
import { useCallback, useEffect, useState, Fragment } from 'react'
import { useIntl, type MessageDescriptor } from 'react-intl'

import type { EuiFieldTextProps, EuiSuperSelectOption } from '@elastic/eui'
import {
  EuiAccordion,
  EuiButton,
  EuiCallOut,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormFieldset,
  EuiFormLabel,
  EuiFormRow,
  EuiHorizontalRule,
  EuiIcon,
  EuiLink,
  EuiPanel,
  EuiSpacer,
  EuiSplitPanel,
  EuiSuperSelect,
  EuiText,
  EuiTitle,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { Region } from '@modules/cluster-user-api/v1/types'
import {
  useBoundProviderAndRegionState,
  useCreateProject,
} from '@modules/project-creation-lib/hooks'
import { isProjectType } from '@modules/project-creation-lib/types'
import { pricingLinkBuilder, projectTypeStartingUrl } from '@modules/project-creation-lib/urls'
import type {
  ElasticsearchOptimizedFor,
  ObservabilityProjectProductTier,
} from '@modules/project-user-api/v1/types'
import type { ProjectType } from '@modules/ui-types/projects'
import history from '@modules/utils/history'
import genericMessages from '@modules/project-lib/genericMessages'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'
import { useSaasUser } from '@modules/profile-lib/hooks'
import type { NonEmptyArray } from '@modules/ts-utils'
import { useSearchAILakeAvailable } from '@modules/project-lib/hooks/useSearchAILakeAvailable'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

import { DEFAULT_VALUE } from '../project-components/ProjectPage/ProductDefinition'

import ProjectCreationContainer from './ProjectCreationContainer'
import ProviderSelector from './ProviderSelector'
import SelectSearchSubType from './SelectSearchSubType'
import { SelectSecuritySubType } from './SelectSecuritySubType'
import SelectObservabilityTier from './SelectObservabilityTier'
import observabilityTiersOptions from './observabilityTiersOptions'

import type { FunctionComponent } from 'react'

type Props = {
  solution: {
    label: ProjectType
    title: MessageDescriptor
    description: MessageDescriptor
  }
  regions: NonEmptyArray<Region>
}

const ProjectCreation: FunctionComponent<Props> = ({
  regions,
  solution: { label, title, description },
}) => {
  const { formatMessage } = useIntl()

  const [isO11yProjectSettingsOpen, setO11yProjectSettingsOpen] = useState(false)

  const [projectName, setProjectNameValue] = useState<string>('')

  const [defaultProjectName, setDefaultProjectName] = useState<string>('')

  const [optimizedFor, setOptimizedFor] = useState<ElasticsearchOptimizedFor>('general_purpose')
  const [observabilityProductTier, setObservabilityProductTier] =
    useState<ObservabilityProjectProductTier>('complete')
  const [securityProductTypes, setSecurityProductTypes] = useState(DEFAULT_VALUE)
  const { isSearchAILakeAvailable } = useSearchAILakeAvailable()

  const {
    selectedRegion,
    selectedProvider,
    providers,
    providerRegions,
    setSelectedProvider,
    setSelectedRegion,
  } = useBoundProviderAndRegionState(regions)

  const regionOptions: Array<EuiSuperSelectOption<Region>> = providerRegions.map((region) => ({
    value: region,
    inputDisplay: region.name,
  }))

  const onProjectNameChange: EuiFieldTextProps['onChange'] = useCallback((event) => {
    setProjectNameValue(event.target.value)
  }, [])

  useEffect(() => {
    setDefaultProjectName(
      formatMessage(genericMessages.defaultProjectName, {
        label: capitalize(label),
      }),
    )
  }, [formatMessage, label])

  const { createProject, isLoading, isError, projectError, organizationError } = useCreateProject()

  const saasUser = useSaasUser()
  const isTrial = saasUser?.subscription.is_trial

  const { euiTheme } = useEuiTheme()

  const [_, flags] = useFlagsWhenLoaded()
  const showObservabilityProjectTiers = flags.observabilityProjectTiers

  if (!isProjectType(label)) {
    return null
  }

  const buttonO11yContent = () => {
    if (isO11yProjectSettingsOpen) {
      return null
    }

    return (
      <EuiText size='xs' css={css({ color: euiTheme.colors.textPrimary })}>
        <FormattedMessage
          id='accordion-o11y-project-settings-button.expand'
          defaultMessage='Edit settings'
        />
      </EuiText>
    )
  }

  const extraO11yAction = () => {
    if (isO11yProjectSettingsOpen) {
      return null
    }

    const o11yProjectTitle =
      observabilityTiersOptions.find((opt) => opt.slug === observabilityProductTier)?.title
        .defaultMessage ?? 'n/a'
    return (
      <EuiText size='s' grow={true} css={css({ marginRight: '10px' })}>
        {o11yProjectTitle}
      </EuiText>
    )
  }

  return (
    <ProjectCreationContainer
      title={
        <Fragment>
          <EuiTitle size='m'>
            <h1>
              <FormattedMessage
                id='create-project-component.fullymanaged-supertitle'
                defaultMessage='Confirm your project settings'
              />
            </h1>
          </EuiTitle>
          <EuiSpacer size='xs' />
        </Fragment>
      }
      step={2}
    >
      <EuiFlexGroup direction='column' alignItems='center'>
        <EuiFlexItem>
          <EuiFlexGroup direction='column' alignItems='center' gutterSize='s'>
            <EuiFlexItem>
              <EuiText color='subdued' textAlign='center'>
                <p>
                  <FormattedMessage {...description} />
                </p>
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiSplitPanel.Outer
            /** min-width has to be greater than 927px to avoid Security Product type options get cropped */
            css={css`
              min-width: ${isSearchAILakeAvailable ? 'auto' : '768px'};
              max-width: ${isSearchAILakeAvailable ? '1258px' : '960px'};

              @media only screen and (max-width: ${euiTheme.breakpoint.m}) {
                width: 100%;
              }
            `}
          >
            <EuiSplitPanel.Inner paddingSize='xl'>
              <EuiFlexGroup alignItems='center' gutterSize='s'>
                <EuiIcon type={`logo${capitalize(label)}`} size='xl' />
                <EuiTitle data-test-subj={`project-creation-title-${label}`} size='s'>
                  <h2>
                    <FormattedMessage {...title} />
                  </h2>
                </EuiTitle>
              </EuiFlexGroup>
              <EuiHorizontalRule />
              <EuiFlexItem>
                <EuiFormRow
                  label={
                    <FormattedMessage id='create-project-component.name' defaultMessage='Name' />
                  }
                  fullWidth={true}
                >
                  <EuiFieldText
                    value={projectName}
                    placeholder={defaultProjectName}
                    onChange={onProjectNameChange}
                    fullWidth={true}
                  />
                </EuiFormRow>
              </EuiFlexItem>

              {label === 'elasticsearch' && (
                <Fragment>
                  <EuiSpacer size='l' />
                  <SelectSearchSubType value={optimizedFor} onChange={setOptimizedFor} />
                </Fragment>
              )}

              {label === 'security' && isSearchAILakeAvailable && (
                <EuiFlexItem>
                  <EuiSpacer size='l' />
                  <EuiFormRow
                    fullWidth={isSearchAILakeAvailable}
                    label={
                      <FormattedMessage
                        id='create-project-component.security-product-feature'
                        defaultMessage='Product feature'
                      />
                    }
                  >
                    <SelectSecuritySubType
                      securityProductTypes={securityProductTypes}
                      setSecurityProductTypes={setSecurityProductTypes}
                    />
                  </EuiFormRow>
                </EuiFlexItem>
              )}

              <EuiSpacer size='l' />
              <EuiFormFieldset
                legend={{
                  children: (
                    <FormattedMessage
                      id='create-project-component.settings'
                      defaultMessage='Settings'
                    />
                  ),
                }}
              >
                <EuiSpacer size='s' />
                <EuiFlexItem>
                  <ProviderSelector
                    value={selectedProvider}
                    availableProviders={providers}
                    onChange={setSelectedProvider}
                  />

                  <EuiSpacer size='m' />

                  <EuiSuperSelect
                    fullWidth={true}
                    prepend={
                      // eslint-disable-next-line @elastic/eui/prefer-css-attributes-for-eui-components
                      <EuiFormLabel style={{ width: '150px' }}>
                        <FormattedMessage id='project-creation.region' defaultMessage='Region' />
                      </EuiFormLabel>
                    }
                    valueOfSelected={selectedRegion}
                    options={regionOptions}
                    onChange={setSelectedRegion}
                  />
                  <EuiSpacer size='m' />
                </EuiFlexItem>
              </EuiFormFieldset>
              {label === 'observability' && showObservabilityProjectTiers && (
                <Fragment>
                  <EuiSpacer size='l' />
                  <EuiFormFieldset
                    legend={{
                      children: (
                        <FormattedMessage
                          id='create-project-o11y-component.settings'
                          defaultMessage='Observability project settings'
                        />
                      ),
                    }}
                  >
                    <EuiAccordion
                      css={css`
                        .euiAccordion__triggerWrapper {
                          flex-direction: row-reverse;
                        }
                      `}
                      id='o11yProjectSetting'
                      buttonContent={buttonO11yContent()}
                      arrowDisplay='none'
                      initialIsOpen={false}
                      extraAction={extraO11yAction()}
                      onToggle={() => setO11yProjectSettingsOpen(!isO11yProjectSettingsOpen)}
                    >
                      <EuiSpacer size='s' />
                      <SelectObservabilityTier
                        hideTitle={true}
                        value={observabilityProductTier}
                        onChange={setObservabilityProductTier}
                      />
                    </EuiAccordion>
                  </EuiFormFieldset>
                </Fragment>
              )}
            </EuiSplitPanel.Inner>
            <EuiPanel paddingSize='xl'>
              <EuiFlexGroup justifyContent='flexEnd' alignItems='center'>
                <EuiFlexItem>
                  <EuiText size='s' color='subdued'>
                    {label === 'elasticsearch' ? (
                      <Fragment>
                        <FormattedMessage
                          id='project-creation.create-es-project-pricing'
                          defaultMessage='Pay for the performance you need. {link}.'
                          values={{
                            link: (
                              <EuiLink
                                href={pricingLinkBuilder(label)}
                                rel='noopener noreferrer'
                                target='_blank'
                              >
                                <FormattedMessage
                                  id='project-creation.create-project-pricing-learnmore'
                                  defaultMessage='See pricing details'
                                />
                              </EuiLink>
                            ),
                          }}
                        />
                        {isTrial && (
                          <Fragment>
                            <br />
                            <FormattedMessage
                              id='project-creation.create-es-project-trial-pricing'
                              defaultMessage='Free during the <strong>trial period</strong>.'
                              values={{
                                strong: (content) => <strong>{content}</strong>,
                              }}
                            />
                          </Fragment>
                        )}
                      </Fragment>
                    ) : (
                      <FormattedMessage
                        id='project-creation.create-project-pricing'
                        defaultMessage='Pricing is based on how you consume {title}. {link}'
                        values={{
                          title: <FormattedMessage {...title} />,
                          link: (
                            <EuiLink
                              href={pricingLinkBuilder(label)}
                              rel='noopener noreferrer'
                              target='_blank'
                            >
                              <FormattedMessage
                                id='project-creation.create-project-pricing-learnmore'
                                defaultMessage='See pricing details'
                              />
                            </EuiLink>
                          ),
                        }}
                      />
                    )}
                  </EuiText>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton
                    fill={true}
                    onClick={handleCreateProject}
                    isLoading={isLoading}
                    data-test-subj='create-project-button'
                    data-test-id='create-project-button'
                  >
                    <FormattedMessage
                      id='project-creation.create-project'
                      defaultMessage='Create serverless project'
                    />
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
              {isError && (
                <EuiFlexItem>
                  <EuiSpacer size='m' />
                  {renderErrors()}
                </EuiFlexItem>
              )}
            </EuiPanel>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
      </EuiFlexGroup>
    </ProjectCreationContainer>
  )

  function renderErrors() {
    if (organizationError) {
      return (
        <EuiCallOut
          title={
            <FormattedMessage
              id='alert.something-went-wrong'
              defaultMessage='Something went wrong'
            />
          }
          color='danger'
        >
          {organizationError.message}
        </EuiCallOut>
      )
    }

    if (projectError) {
      if (typeof projectError === 'string') {
        return <ProjectApiErrorCallout error={projectError} />
      }

      return (
        <ProjectApiErrorCallout
          error={projectError}
          children={(error) => {
            if (error?.code === 'projects.create_project.max_trial_projects.forbidden') {
              return formatMessage({
                id: 'project-creation.too-many-projects',
                defaultMessage: 'Your organization has reached the current project limit.',
              })
            }

            return error?.message
          }}
        />
      )
    }

    return null
  }

  function handleCreateProject() {
    createProject({
      projectName: projectName || defaultProjectName,
      region: selectedRegion.id,
      label,
      optimizedFor,
      observabilityProjectTier: observabilityProductTier,
      productTypes: securityProductTypes,
      onSuccess: (data) => history.push(projectTypeStartingUrl(data.type, data.id)),
    })
  }
}

export default ProjectCreation
